import type { ProtocolData } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";

type ProtocolReserveTotals = Partial<
    Pick<
        ProtocolData,
        "custodyNotionalUsd" | "vaultNotionalUsd" | "yieldDistributorsNotionalUsd" | "reserveFundNotionalUsd"
    >
>;

function toFiniteNumber(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export function calculateProtocolReserveValue(data: ProtocolReserveTotals | null | undefined): number | null {
    if (!data) return null;

    const values = [
        data.custodyNotionalUsd,
        data.vaultNotionalUsd,
        data.yieldDistributorsNotionalUsd,
        data.reserveFundNotionalUsd
    ].map(toFiniteNumber);

    if (values.some((value) => value === null)) return null;

    // Derivatives notional measures market exposure, not reserve NAV. It must
    // not be counted as an owned reserve asset in the backing numerator.
    return values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
}

export function calculateBackingRatio(reserveValue: number, totalSupply: number): number | null {
    if (!Number.isFinite(reserveValue) || !Number.isFinite(totalSupply)) {
        return null;
    }
    if (reserveValue < 0 || totalSupply <= 0) return null;
    return (reserveValue / totalSupply) * 100;
}

export function formatBackingRatio(value: number | null): string {
    return value === null ? "—" : `${value.toFixed(2)}%`;
}

/** Keep exact token units and floor the display without converting through a float. */
export function earningSupplyDisplayAmount(value: string | null | undefined): BigDecimal | null {
    if (typeof value !== "string" || !/^\d+(\.\d+)?$/.test(value)) return null;
    return new BigDecimal(value.split(".")[0]);
}
